// MARK://dhk8/pos/components/BranchSwitcher
// Sucursal ACTIVA del POS — visible siempre y cambiable.
//
// 🔴 Por qué existe: `floorMaps` devuelve los planos de TODO el tenant y 18
// vienen marcados `isDefault`, así que el POS pintaba "el primero que llegara":
// un mesero de López Cotilla operaba las mesas de Tabachines sin enterarse.
// Ahora la sucursal se elige y se ve; el plano, los QRs y la caja cuelgan de ella.
import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Building2, ChevronDown } from 'lucide-react'
import { listFloorMaps } from '../lib/clients/floor.js'
import { setActiveBranchId } from '../lib/api.js'
import { useActiveBranchId } from '../hooks/useActiveBranch.js'

interface Sucursal { branchId: string; nombre: string }

/** "Plano López Cotilla" → "López Cotilla". El plano lleva el nombre del local. */
function nombreDeSucursal(nombrePlano: string): string {
  return nombrePlano.replace(/^plano\s+/i, '').trim() || nombrePlano
}

export function BranchSwitcher() {
  const qc = useQueryClient()
  const activa = useActiveBranchId()
  const [sucursales, setSucursales] = useState<Sucursal[]>([])
  const [abierto, setAbierto] = useState(false)

  useEffect(() => {
    let vivo = true
    void (async () => {
      try {
        const maps = await listFloorMaps()
        if (!vivo) return
        const vistas = new Map<string, string>()
        for (const m of maps) {
          const bid = (m as { branchId?: string | null }).branchId
          if (typeof bid === 'string' && bid.length > 0 && !vistas.has(bid)) {
            vistas.set(bid, nombreDeSucursal(m.name))
          }
        }
        setSucursales([...vistas].map(([branchId, nombre]) => ({ branchId, nombre }))
          .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')))
      } catch { /* sin planos → no se pinta el selector */ }
    })()
    return () => { vivo = false }
  }, [])

  if (sucursales.length === 0) return <></>
  const actual = sucursales.find((s) => s.branchId === activa) ?? sucursales[0]!

  function elegir(branchId: string): void {
    setActiveBranchId(branchId)
    setAbierto(false)
    // El plano, la cocina y la caja cuelgan de la sucursal → refrescar todo.
    void qc.invalidateQueries()
  }

  return (
    <div className="pos-branch">
      <button type="button" className="pos-branch__btn" onClick={() => setAbierto((v) => !v)}
        aria-haspopup="listbox" aria-expanded={abierto}
        title="Sucursal activa — el plano, los QRs y la caja son de ésta">
        <Building2 size={14} />
        <span className="pos-branch__name">{actual.nombre}</span>
        {sucursales.length > 1 && <ChevronDown size={13} />}
      </button>
      {abierto && sucursales.length > 1 && (
        <>
          <button type="button" className="pos-branch__scrim" aria-label="Cerrar" onClick={() => setAbierto(false)} />
          <ul className="pos-branch__list" role="listbox">
            {sucursales.map((s) => (
              <li key={s.branchId}>
                <button type="button" role="option" aria-selected={s.branchId === actual.branchId}
                  className={`pos-branch__opt${s.branchId === actual.branchId ? ' is-on' : ''}`}
                  onClick={() => elegir(s.branchId)}>
                  {s.nombre}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      <style>{`
        .pos-branch { position: relative; }
        .pos-branch__btn { display: inline-flex; align-items: center; gap: 6px;
          background: var(--pos-tint-fill); color: var(--pos-fg);
          border: 1px solid var(--pos-separator); border-radius: 999px;
          padding: 5px 12px; font-size: var(--pos-text-xs); font-weight: 600;
          cursor: pointer; max-width: 42vw; }
        .pos-branch__btn:hover { background: var(--pos-tint-fill-hover); }
        .pos-branch__name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .pos-branch__scrim { position: fixed; inset: 0; background: transparent; border: 0; z-index: 40; }
        .pos-branch__list { position: absolute; top: calc(100% + 6px); left: 50%; transform: translateX(-50%);
          z-index: 41; margin: 0; padding: 6px; list-style: none; min-width: 220px; max-height: 60vh; overflow-y: auto;
          background: var(--pos-elevated); color: var(--pos-fg);
          border: 1px solid var(--pos-separator); border-radius: var(--pos-radius-md);
          box-shadow: var(--pos-shadow-4); }
        .pos-branch__opt { display: block; width: 100%; text-align: left; background: none; border: 0; cursor: pointer;
          color: var(--pos-fg); padding: 9px 12px; border-radius: var(--pos-radius-md); font-size: var(--pos-text-sm); }
        .pos-branch__opt:hover { background: var(--pos-tint-fill-hover); }
        .pos-branch__opt.is-on { color: var(--pos-tint-on); font-weight: 700; }
      `}</style>
    </div>
  )
}
